import xss from "xss";

/**
 * Common XSS configuration that allows safe HTML elements including SVG and iframe
 * @returns {Object} XSS configuration object with whitelist for safe HTML elements
 */
export const getXssOptions = () => ({
  whiteList: {
    // Allow common HTML elements
    a: ['href', 'title', 'target'],
    abbr: ['title'],
    acronym: ['title'],
    b: [],
    blockquote: ['cite'],
    code: [],
    em: [],
    i: [],
    li: ['class', 'style'],
    ol: ['class', 'style'],
    strong: [],
    u: [],
    ul: ['class', 'style'],
    
    // Allow iframe with safe attributes (for video embeds)
    iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'allow', 'style', 'class', 'title'],
    
    // Allow SVG elements with all their attributes (for icons and graphics)
    svg: ['xmlns', 'role', 'width', 'height', 'viewBox', 'fill', 'class', 'style'],
    path: ['d', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'class', 'style'],
    rect: ['x', 'y', 'width', 'height', 'fill', 'stroke', 'class', 'style'],
    circle: ['cx', 'cy', 'r', 'fill', 'stroke', 'class', 'style'],
    ellipse: ['cx', 'cy', 'rx', 'ry', 'fill', 'stroke', 'class', 'style'],
    line: ['x1', 'y1', 'x2', 'y2', 'stroke', 'stroke-width', 'class', 'style'],
    polyline: ['points', 'fill', 'stroke', 'stroke-width', 'class', 'style'],
    polygon: ['points', 'fill', 'stroke', 'stroke-width', 'class', 'style'],
    g: ['class', 'style', 'transform'],
    defs: [],
    clipPath: ['id', 'class', 'style'],
    mask: ['id', 'class', 'style'],
    
    // Allow other common elements
    p: ['class', 'style'],
    div: ['class', 'style', 'id', 'overflow'],
    span: ['class', 'style'],
    h1: ['class', 'style', 'id'],
    h2: ['class', 'style', 'id'],
    h3: ['class', 'style', 'id'],
    h4: ['class', 'style', 'id'],
    h5: ['class', 'style', 'id'],
    h6: ['class', 'style', 'id'],
    br: [],
    hr: [],
    img: ['src', 'alt', 'title', 'width', 'height', 'class', 'style', 'loading'],
    // Enhanced table support - allow all necessary attributes for proper table rendering
    table: ['class', 'style', 'border', 'cellpadding', 'cellspacing', 'width', 'border-collapse'],
    tr: ['class', 'style'],
    td: ['class', 'style', 'colspan', 'rowspan', 'border', 'padding', 'font-weight', 'scope'],
    th: ['class', 'style', 'colspan', 'rowspan', 'border', 'padding', 'font-weight', 'scope'],
    thead: ['class', 'style'],
    tbody: ['class', 'style'],
    tfoot: ['class', 'style'],
    caption: ['class', 'style', 'font-weight', 'margin-bottom'],
    colgroup: ['class', 'style'],
    col: ['class', 'style', 'span'],
    
    // Allow form elements if needed
    form: ['action', 'method', 'class', 'style'],
    input: ['type', 'name', 'value', 'class', 'style', 'placeholder', 'required'],
    textarea: ['name', 'class', 'style', 'placeholder', 'required', 'rows', 'cols'],
    button: ['type', 'class', 'style'],
    select: ['name', 'class', 'style'],
    option: ['value', 'class', 'style', 'selected'],
    label: ['for', 'class', 'style'],
    fieldset: ['class', 'style'],
    legend: ['class', 'style'],
    
    // HTML5 Semantic Elements - comprehensive coverage
    section: ['class', 'style', 'id'],
    article: ['class', 'style', 'id'],
    aside: ['class', 'style', 'id'],
    main: ['class', 'style', 'id'],
    header: ['class', 'style', 'id'],
    footer: ['class', 'style', 'id'],
    nav: ['class', 'style', 'id'],
    figure: ['class', 'style', 'id'],
    figcaption: ['class', 'style'],
    details: ['class', 'style', 'open'],
    summary: ['class', 'style'],
    mark: ['class', 'style'],
    time: ['class', 'style', 'datetime'],
    small: ['class', 'style'],
    cite: ['class', 'style'],
    q: ['class', 'style', 'cite'],
    del: ['class', 'style', 'cite', 'datetime'],
    ins: ['class', 'style', 'cite', 'datetime'],
    sub: ['class', 'style'],
    sup: ['class', 'style'],
    kbd: ['class', 'style'],
    samp: ['class', 'style'],
    var: ['class', 'style'],
    pre: ['class', 'style'],
    address: ['class', 'style'],
    dl: ['class', 'style'],
    dt: ['class', 'style'],
    dd: ['class', 'style'],
    
    // Additional common elements
    noscript: [],
    script: ['type', 'src', 'async', 'defer'],
    style: ['type', 'media'],
    link: ['rel', 'href', 'type', 'media', 'sizes'],
    meta: ['name', 'content', 'http-equiv', 'charset'],
    title: [],
    head: [],
    body: ['class', 'style'],
    html: ['lang', 'class', 'style'],
    
    // Allow data attributes for dynamic content
    '*': ['data-*', 'aria-*', 'role']
  },
  // Allow safe CSS properties
  css: {
    whiteList: [
      'color', 'background-color', 'font-size', 'font-weight', 'text-align',
      'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
      'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
      'border', 'border-collapse', 'border-radius', 'border-width', 'border-style', 'border-color',
      'display', 'position', 'top', 'left', 'right', 'bottom', 
      'width', 'height', 'max-width', 'min-width', 'max-height', 'min-height', 
      'overflow', 'z-index'
    ]
  }
});

/**
 * Sanitize HTML content using the common XSS configuration
 * @param {string} html - HTML content to sanitize
 * @returns {string} Sanitized HTML content
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return xss(html, getXssOptions());
};
